import MapboxDraw, { DrawCustomModeThis, DrawFeature, DrawLineString, DrawPoint } from "@mapbox/mapbox-gl-draw";
import booleanEqual from "@turf/boolean-equal";
import booleanPointOnLine from "@turf/boolean-point-on-line";
import { Feature, LineString } from "@turf/helpers";
import { defer } from "lodash";

import { DRAWING_CONSTANTS } from "fond/constants";
import { inPole } from "fond/layers";
import { LayerIds } from "fond/types";

import { pullInFeatures as pullInAerialFeatures } from "../topologyHelper";

import { pullInFeatures, splitPaths } from "./helper";

const { constants: Constants } = MapboxDraw;

/**
 * Aerial config for checking collocated aerial poles at endpoints.
 * We need to check for both underground points AND aerial poles when
 * deciding whether to create a new point at a path endpoint.
 */
const aerialConfig = { lineLayerId: LayerIds.inSpan, pointLayerId: inPole };

/**
 * onCreate callback for underground paths.
 *
 * Called by the base drawLineString.onStop after the line is finalized (trailing cursor vertex already removed).
 * Handles:
 * 1. Replaces the drawn feature with one tagged with the correct layerId
 * 2. Creates underground points at the first and last endpoint only (not internal nodes)
 * 3. Skips creating a point where an underground point or aerial pole already exists
 * 4. Splits any existing underground paths at the new endpoint locations
 * 5. Fires a single draw.create event for all new features
 */
export function onCreateUndergroundPath(this: DrawCustomModeThis, feature: DrawFeature, state: Record<string, unknown>): void {
  const drawnLine = feature as DrawLineString;
  const finalCoords = drawnLine.getCoordinates();

  if (finalCoords.length < 2) {
    // Not enough vertices for a valid line — delete it
    this.deleteFeature(drawnLine.id as string, { silent: true });
    return;
  }

  const newFeatures: DrawFeature[] = [];

  // Create the underground path with the correct layerId
  const path = this.newFeature({
    type: Constants.geojsonTypes.FEATURE,
    properties: { layerId: LayerIds.inStreet },
    geometry: {
      type: Constants.geojsonTypes.LINE_STRING,
      coordinates: finalCoords,
    },
  }) as DrawLineString;
  this.addFeature(path);
  newFeatures.push(path);

  // Create points at endpoints only (first and last coordinate)
  const endpoints = [finalCoords[0], finalCoords[finalCoords.length - 1]];

  // Track lines already split so that if both endpoints land on the same
  // existing path we split the replacement segment instead of the
  // (already-deleted) original a second time.
  const alreadySplit = new Map<string | number, DrawLineString[]>();

  endpoints.forEach((coordinates) => {
    const tempPoint = this.newFeature({
      type: Constants.geojsonTypes.FEATURE,
      properties: { layerId: LayerIds.inStreetPoints, [DRAWING_CONSTANTS.Unsnapped]: "false" },
      geometry: {
        type: Constants.geojsonTypes.POINT,
        coordinates,
      },
    }) as DrawPoint;

    // Check for existing underground points and nearby lines at this location
    const { points: existingUgPoints, lines: nearbyLines } = pullInFeatures.call(this, tempPoint);

    // Check for existing aerial poles at this location
    const { points: existingPoles } = pullInAerialFeatures.call(this, aerialConfig, tempPoint);

    if (existingUgPoints.length > 0 || existingPoles.length > 0) {
      // Point already exists at this location — don't create duplicate
    } else {
      this.addFeature(tempPoint);
      newFeatures.push(tempPoint);

      // Split any existing underground paths at this point
      nearbyLines
        .filter(({ id }) => id !== path.id)
        .forEach((line) => {
          const replacements = alreadySplit.get(line.id);
          if (replacements) {
            // This line was already split at the other endpoint — split the
            // appropriate replacement segment instead of the original.
            replacements.forEach((seg) => {
              if (booleanPointOnLine(coordinates, seg.toGeoJSON() as Feature<LineString>, { epsilon: 0.0000001 })) {
                const result = splitPaths.call(this, seg, tempPoint);
                // Remove any split result that duplicates the drawn path's geometry.
                result.forEach((newSeg) => {
                  if (booleanEqual(newSeg, path, { precision: 6 })) {
                    this.deleteFeature(newSeg.id as string, { silent: true });
                  }
                });
              }
            });
          } else {
            const result = splitPaths.call(this, line, tempPoint);
            if (result.length > 0) {
              alreadySplit.set(line.id, result);
            }
          }
        });
    }
  });

  // Delete the original drawn feature (we replaced it with our own)
  this.deleteFeature(drawnLine.id as string, { silent: true });

  // Fire a single draw.create for all new features
  const geoJsonFeatures = newFeatures.map((f) => f.toGeoJSON());
  this.map.fire("draw.create", {
    features: geoJsonFeatures,
  });

  // Defer setting the id property to match the MapboxDraw-assigned id
  defer(() => {
    newFeatures.forEach((f) => {
      f.setProperty("id", f.id);
    });
  });
}
